/**
 * Invalid Traffic (IVT) Session Guard
 *
 * AdSense policy bans any artificial inflation of impressions or clicks.
 * This guard detects session-level anomalies and disables ad rendering
 * for that session when suspicious behaviour is detected.
 *
 * Signals monitored:
 *   1. Rapid page-view rate (>6 distinct paths in 30 s → bot-like navigation)
 *   2. Absence of any user interaction in the first 5 s (no mouse/touch/key)
 *   3. Referrer is a known spam/scraper domain prefix list
 *
 * When flagged the flag is stored in sessionStorage so it persists across
 * the tab's lifetime but clears when the user opens a new session.
 */

const SESSION_FLAG_KEY = 'zodiage_ivt_flag';
const PAGE_LOG_KEY     = 'zodiage_ivt_pages';
const RAPID_NAV_LIMIT  = 6;    // paths within the window below
const RAPID_NAV_WINDOW = 30_000; // 30 seconds

const SPAM_REFERRER_PREFIXES = [
  'semalt',
  'buttons-for-website',
  'best-seo-solution',
  'Get-Free-Traffic-Now',
  'social-buttons',
  'o-o-6-o-o',
];

function setFlagged() {
  try { sessionStorage.setItem(SESSION_FLAG_KEY, '1'); } catch (_) {}
}

export function isTrafficFlagged(): boolean {
  try {
    return sessionStorage.getItem(SESSION_FLAG_KEY) === '1';
  } catch (_) {
    return false;
  }
}

/** Call once per page navigation. Returns true if the session is now flagged. */
export function recordPageView(path: string): boolean {
  if (isTrafficFlagged()) return true;

  // --- Rapid navigation check ---
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem(PAGE_LOG_KEY);
    const log: Array<{ path: string; ts: number }> = raw ? JSON.parse(raw) : [];

    // Drop entries older than the window
    const recent = log.filter(e => now - e.ts < RAPID_NAV_WINDOW);
    recent.push({ path, ts: now });
    sessionStorage.setItem(PAGE_LOG_KEY, JSON.stringify(recent));

    // Count unique paths in window
    const unique = new Set(recent.map(e => e.path));
    if (unique.size > RAPID_NAV_LIMIT) {
      setFlagged();
      return true;
    }
  } catch (_) {}

  // --- Spam referrer check (only on first load) ---
  try {
    const ref = document.referrer.toLowerCase();
    if (ref && SPAM_REFERRER_PREFIXES.some(p => ref.includes(p))) {
      setFlagged();
      return true;
    }
  } catch (_) {}

  return false;
}

/** Wire this up once in App.tsx to detect zero-interaction sessions. */
export function initInteractionWatch() {
  if (isTrafficFlagged()) return;

  let interacted = false;
  const mark = () => { interacted = true; };

  window.addEventListener('mousemove', mark, { once: true, passive: true });
  window.addEventListener('touchstart', mark, { once: true, passive: true });
  window.addEventListener('keydown',    mark, { once: true, passive: true });
  window.addEventListener('scroll',     mark, { once: true, passive: true });

  // After 5 s with no interaction at all, flag as non-human
  setTimeout(() => {
    window.removeEventListener('mousemove', mark);
    window.removeEventListener('touchstart', mark);
    window.removeEventListener('keydown',    mark);
    window.removeEventListener('scroll',     mark);
    if (!interacted) setFlagged();
  }, 5_000);
}
